import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {
  SharedModule,
  StringCustomFieldConfig,
  FormInputComponent,
  NotificationService,
  ModalService,
  DynamicFormInputComponent,
} from '@vendure/admin-ui/core';
import { distinctUntilChanged, Subscription } from 'rxjs';

import {
  ErpIntegrationSettings,
  ErpTypes,
  ErpLingXingLogisticsSetting
} from '../../common/ui/ui-types';
import { safeJSONParse } from '../../common/ui/utils';
import { UpdateLingXingStoreDialogComponent } from './erp-lingxing-store-selector-dialog.component';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';

@Component({
  selector: 'erp-settings-form',
  template: `
  <div class="integration-panel">
    <div class="integration-header">
      <div class="integration-info">
        <div class="integration-title">ERP Integration</div>
        <div class="integration-description">Sync inventory, orders, and data. </div>
      </div>
      <clr-toggle-wrapper class="toggle-right">
        <input
          type="checkbox"
          clrToggle
          name="erpEnabled"
          [formControl]="enabledControl"
          [disabled]="readonly"
        />
      </clr-toggle-wrapper>
    </div>

    <div *ngIf="enabledControl.value" class="integration-fields">
      <!-- Integration Type -->
      <div class="settings-section">
        <vdr-form-field label="Integration Type">
          <select [formControl]="typeControl" [disabled]="readonly">
            <option *ngFor="let t of availableTypeEntries" [value]="t.value">{{ t.key }} ({{ t.value }})</option>
          </select>
        </vdr-form-field>
      </div>

      <!-- LingXing Settings -->
      <div *ngIf="[ErpTypes.LingXing].includes(typeControl.value)" class="settings-section">
        <div class="section-title">API Credentials</div>
        <div class="form-grid">
          <vdr-form-field label="App ID">
            <input type="text" [formControl]="appIdControl" [disabled]="readonly" />
          </vdr-form-field>
          <vdr-form-field label="App Secret">
            <input type="password" autocomplete="new-password" [formControl]="appSecretControl" [disabled]="readonly" />
          </vdr-form-field>
        </div>
      </div>

      <!-- Store Connection -->
      <div *ngIf="[ErpTypes.LingXing].includes(typeControl.value)" class="settings-section">
        <div class="section-title">Store Connection</div>
        <div class="store-connection-row">
          <div class="store-info">
            <vdr-form-field label="Store Name">
              <input type="text" [value]="storeNameControl.value || '-'" readonly />
            </vdr-form-field>
            <vdr-form-field label="Store ID">
              <input type="text" [value]="storeIdControl.value || '-'" readonly />
            </vdr-form-field>
          </div>
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            (click)="updateLingXingStores()"
            [disabled]="readonly || !appIdControl.value || !appSecretControl.value"
          >
            <clr-icon shape="store"></clr-icon>
            Update Platform Store
          </button>
        </div>
      </div>

      <!-- LingXing Logistics Settings -->
      <div *ngIf="typeControl.value === ErpTypes.LingXing" class="settings-section">
        <div class="section-title">Logistics Tracking Source Settings</div>
        <vdr-dynamic-form-input
          #logisticsSettingsInput
          [control]="logisticsSettingsUiControl"
          [readonly]="readonly"
          [def]="{
            list: true,
            type: 'string',
            ui: { component: 'erp-lingxing-logistics-setting-form' }
          }"
        >
        </vdr-dynamic-form-input>
      </div>
    </div>
  </div>
  `,
  styles: [`
    .integration-panel {
      padding: 16px;
      border: 1px solid var(--color-component-border-100);
      border-radius: 4px;
      background: white;
    }

    .integration-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .integration-info {
      flex: 1;
    }

    .integration-title {
      font-weight: 600;
      font-size: 14px;
      color: var(--color-text-100);
      margin-bottom: 4px;
    }

    .integration-description {
      font-size: 13px;
      color: var(--color-text-300);
      line-height: 1.4;
    }

    .toggle-right {
      margin-left: 16px;
      flex-shrink: 0;
    }

    .integration-fields {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid var(--color-component-border-100);
    }

    .settings-section {
      padding: 12px 0;
      border-bottom: 1px solid var(--color-component-border-100);
    }

    .settings-section:first-child {
      padding-top: 0;
    }

    .settings-section:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    .section-title {
      font-weight: 500;
      font-size: 13px;
      color: var(--color-text-200);
      margin-bottom: 6px;
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    .store-connection-row {
      display: flex;
      align-items: flex-end;
      gap: 16px;
    }

    .store-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      flex: 1;
    }

    .btn-sm {
      padding: 6px 12px;
      font-size: 12px;
      white-space: nowrap;
    }

    vdr-form-field {
      margin-bottom: 0;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [SharedModule],
})
export class ErpSettingsFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, OnDestroy {

  @ViewChild('logisticsSettingsInput', { static: false })
  set logisticsSettingsInputSetter(inputComponent: DynamicFormInputComponent | undefined) {
    if (!inputComponent) return;
    this.attachLogisticsSettingsInput(inputComponent);
  }
  private logisticsInputInstance: DynamicFormInputComponent | null = null;


  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  enabledControl = new FormControl<boolean>(false);
  typeControl = new FormControl<string>('');
  appIdControl = new FormControl<string>('');
  appSecretControl = new FormControl<string>('');
  storeIdControl = new FormControl<string>('');
  storeNameControl = new FormControl<string>('');
  logisticsSettingsUiControl = new UntypedFormControl([]);

  private logisticsSettingsTyped: ErpLingXingLogisticsSetting[] = [];

  ErpTypes = ErpTypes;
  availableTypeEntries: Array<{ key: string; value: string }> =
    Object.entries(ErpTypes).map(([key, value]) => ({ key, value }));

  private subs = new Subscription();

  private currentModel: ErpIntegrationSettings;


  constructor(
    private notificationService: NotificationService,
    private modalService: ModalService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const defaultErpIntegrationSettings: ErpIntegrationSettings = {
      enabled: false,
      type: ErpTypes.LingXing,
      platformSettings: {
        appId: '',
        appSecret: '',
      },
    };

    // Initial model from current control value
    this.currentModel = safeJSONParse<ErpIntegrationSettings>(
      this.formControl.value,
      this.notificationService,
      defaultErpIntegrationSettings,
    )!;

    this.applyModelToControls(this.currentModel);
    this.setupSubscriptions();

    // React to external changes (e.g. channel switch updates parent formControl value)
    this.subs.add(
      this.formControl.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
        const nextModel = safeJSONParse<ErpIntegrationSettings>(
          value,
          this.notificationService,
          defaultErpIntegrationSettings,
        )!;
        this.currentModel = nextModel;
        this.applyModelToControls(this.currentModel);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private attachLogisticsSettingsInput(inputComponent: DynamicFormInputComponent) {
    if (this.logisticsInputInstance === inputComponent) return;
    this.logisticsInputInstance = inputComponent;
    const originalValue =
      this.currentModel?.type === ErpTypes.LingXing && this.logisticsSettingsTyped?.length
        ? this.logisticsSettingsTyped.map(v => JSON.stringify(v))
        : (Array.isArray(this.logisticsSettingsUiControl.value) ? this.logisticsSettingsUiControl.value : []);

    inputComponent.writeValue(originalValue);
    inputComponent.registerOnChange((values: string[]) => {
      const defaultErpLingXingLogisticsSetting = {
          erpLogisticsCarrierName: '',
          trackingNoSource: 'tracking_no' as const,
      };
      this.logisticsSettingsTyped = values?.map(v => safeJSONParse<ErpLingXingLogisticsSetting>(
        v,
        this.notificationService,
        defaultErpLingXingLogisticsSetting
      )).filter(notNullOrUndefined) ?? [];
      this.updateJsonString();
    });
  }

  updateLingXingStores() {
    const type = this.typeControl.value;
    const appId = this.appIdControl.value;
    const appSecret = this.appSecretControl.value;

    this.modalService
      .fromComponent(UpdateLingXingStoreDialogComponent, {
        size: 'md',
        locals: {
          type: type as ErpTypes,
          appId: appId || '',
          appSecret: appSecret || ''
        },
      })
      .subscribe(result => {
        if (result) {
          this.storeIdControl.setValue(result.storeId);
          this.storeNameControl.setValue(result.storeName);
          this.updateJsonString();
        }
      });
  }

  private setupSubscriptions() {
    // Enabled
    this.subs.add(
      this.enabledControl.valueChanges.subscribe(enabled => {
        if (enabled) {
          this.enableAll();
        } else {
          this.disableAllExceptEnabled();
        }
        this.updateJsonString();
      }),
    );

    this.subs.add(
      this.typeControl.valueChanges.pipe(distinctUntilChanged()).subscribe(type => {
        // Clear LingXing-only UI if switching away
        if (type !== ErpTypes.LingXing) {
          this.logisticsSettingsTyped = [];
          this.logisticsSettingsUiControl.setValue([], { emitEvent: false });
        } else {
          // If switching to LingXing with no rows, start empty UI list
          if (!Array.isArray(this.logisticsSettingsUiControl.value)) {
            this.logisticsSettingsUiControl.setValue([], { emitEvent: false });
          }
        }
        this.updateJsonString();
      }),
    );

    this.subs.add(this.appIdControl.valueChanges.subscribe(() => this.updateJsonString()));
    this.subs.add(this.appSecretControl.valueChanges.subscribe(() => this.updateJsonString()));
    this.subs.add(this.storeIdControl.valueChanges.subscribe(() => this.updateJsonString()));
    this.subs.add(this.storeNameControl.valueChanges.subscribe(() => this.updateJsonString()));
  }

  private disableAllExceptEnabled() {
    this.typeControl.disable({ emitEvent: false });
    this.appIdControl.disable({ emitEvent: false });
    this.appSecretControl.disable({ emitEvent: false });
    this.logisticsSettingsUiControl.disable({ emitEvent: false });
  }

  private enableAll() {
    this.typeControl.enable({ emitEvent: false });
    this.appIdControl.enable({ emitEvent: false });
    this.appSecretControl.enable({ emitEvent: false });
    this.logisticsSettingsUiControl.enable({ emitEvent: false });
  }

  /** Apply currentModel to all UI controls without emitting valueChanges */
  private applyModelToControls(model: ErpIntegrationSettings) {
    this.enabledControl.setValue(model.enabled ?? false, { emitEvent: false });
    this.typeControl.setValue(model.type ?? '', { emitEvent: false });

    const ps: any = model.platformSettings ?? {};
    this.appIdControl.setValue(ps.appId ?? '', { emitEvent: false });
    this.appSecretControl.setValue(ps.appSecret ?? '', { emitEvent: false });
    this.storeIdControl.setValue(ps.storeId ?? '', { emitEvent: false });
    this.storeNameControl.setValue(ps.storeName ?? '', { emitEvent: false });

    if (model.type === ErpTypes.LingXing) {
      const savedList: ErpLingXingLogisticsSetting[] = Array.isArray(ps.logisticsSettings) ? ps.logisticsSettings : [];
      this.logisticsSettingsTyped = savedList;
      const uiList = savedList.map(row => JSON.stringify(row));
      this.logisticsSettingsUiControl.setValue(uiList, { emitEvent: false });
      if (this.logisticsInputInstance) {
        this.logisticsInputInstance.writeValue(uiList);
      }
    } else {
      this.logisticsSettingsTyped = [];
      this.logisticsSettingsUiControl.setValue([], { emitEvent: false });
      if (this.logisticsInputInstance) {
        this.logisticsInputInstance.writeValue([]);
      }
    }

    if (!this.enabledControl.value) {
      this.disableAllExceptEnabled();
    } else {
      this.enableAll();
    }
    this.changeDetector.markForCheck();
  }

  private updateJsonString() {
    if (!this.enabledControl.value) {
      const stub = { enabled: false, type: '', platformSettings: {} };
      // Do not emit to avoid feedback loop with valueChanges subscription
      this.formControl.setValue(JSON.stringify(stub), { emitEvent: false });
      this.formControl.markAsDirty();
      return;
    }

    const type = (this.typeControl.value || '') as ErpTypes;
    const platformSettings: any = {
      appId: this.appIdControl.value || '',
      appSecret: this.appSecretControl.value || '',
    };

    if ([ErpTypes.LingXing].includes(type)) {
      platformSettings.storeId = this.storeIdControl.value || '';
      platformSettings.storeName = this.storeNameControl.value || '';
    }

    if (type === ErpTypes.LingXing) {
      platformSettings.logisticsSettings = this.logisticsSettingsTyped || [];
    }

    const model: ErpIntegrationSettings = {
      enabled: !!this.enabledControl.value,
      type,
      platformSettings,
    };

    this.currentModel = model;
    const json = JSON.stringify(model);
    // Do not emit to avoid triggering the external valueChanges listener
    this.formControl.setValue(json, { emitEvent: false });
    this.formControl.markAsDirty();
  }
}